/**
 * HMAC-SHA512 and HKDF, built on tweetnacl's hash (ADR-016 stage 1).
 *
 * The Double Ratchet needs both and tweetnacl has neither. Adding a fourth
 * family of primitives for it costs the same as the AES-GCM option ADR-011 §10
 * rejected: two more ports and two more chances for them to disagree. HMAC over
 * a hash we already ship on all three platforms is fifteen lines and comes with
 * somebody else's test vectors.
 *
 * SHA-512, so the block size is 128 bytes — the classic bug here is writing
 * 64 and getting a construction that agrees with nothing.
 */
import nacl from 'tweetnacl';

const BLOCK = 128;   // SHA-512 block size in bytes
export const HASH_LEN = 64;

function concat(...parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}

export function hmacSha512(key, message) {
  let k = key;
  if (k.length > BLOCK) k = nacl.hash(k);
  const padded = new Uint8Array(BLOCK);
  padded.set(k);

  const inner = new Uint8Array(BLOCK);
  const outer = new Uint8Array(BLOCK);
  for (let i = 0; i < BLOCK; i++) {
    inner[i] = padded[i] ^ 0x36;
    outer[i] = padded[i] ^ 0x5c;
  }
  return nacl.hash(concat(outer, nacl.hash(concat(inner, message))));
}

/** RFC 5869 extract-then-expand, with SHA-512. */
export function hkdf(ikm, salt, info, length) {
  const prk = hmacSha512(salt && salt.length ? salt : new Uint8Array(HASH_LEN), ikm);
  const out = new Uint8Array(length);
  let prev = new Uint8Array(0);
  let off = 0;
  for (let counter = 1; off < length; counter++) {
    if (counter > 255) throw new Error('hkdf: length exceeds what one PRK can expand');
    prev = hmacSha512(prk, concat(prev, info, new Uint8Array([counter])));
    const take = Math.min(prev.length, length - off);
    out.set(prev.subarray(0, take), off);
    off += take;
  }
  return out;
}
